/* eslint-disable react/no-this-in-sfc */
import React, {
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react';
import PropTypes from 'prop-types';
import { shadingMatchesScope, shadingRequestScope } from './utils/shading-scope.js';

import { Canvas, useFrame, useThree } from '@react-three/fiber';

import * as THREE from 'three';

// Components
import { DesignContext } from './contexts/designContext.js';
import Toolbar from './Toolbar.js';
import Scene from './objects/Scene.js';
import ExportWrapper from './objects/ExportWrapper.js';

import useMaterials from './hooks/useMaterials.js';
import useDSM from './hooks/useDSM.js';
import useShader from './hooks/useShader.js';
import useWeatherData from './hooks/useWeatherData.js';
import use3DReconstruction from './hooks/use3DReconstruction.js';
import usePanelOperation from './hooks/usePanelOperation.js';
import useTreeOperation from './hooks/useTreeOperation.js';
import useDrawing from './hooks/useDrawing.js';
import { getShadingRequiredClientDataValidationError } from './shading-input.js';

import './styles.css';

import Drawing from 'dxf-writer';

import {
  optimizeVertexPosition,
  optimizePlanes,
  getParentsPolygon,
  projectToPlane,
  segmentRegression,
  fitPlaneOnSegment,
  projectTo3DFromOrgVertices,
  polygonArea3D,
  getDSMPoints
} from './algorithms/geometry.js';

import { CanvasPool, downloadObjectAsJson, buildShadingCacheKey, getRaycastableObjects } from './algorithms/utils.js';

import { detectEdgeTypes } from './algorithms/edgeDetection.js';
import { initState, sceneUnitToInche } from './algorithms/3DReconstruction.js';
import { getOffset, segmentIndexMask, measureByDSM } from './algorithms/dsm.js';
import { CanvasPoolContext } from './contexts/canvasPoolContext.js';
import { normalizeShadingMediaUrl } from './utils/mediaUrl.js';

let loadingTimeout;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const SHADING_REQUIRED_DATA_RETRY_DELAYS_MS = [2000, 5000];

const getRealPanelIndexes = (segmentPanels) => {
  if (Array.isArray(segmentPanels?.realPanels)) {
    return segmentPanels.realPanels.reduce((indexes, isReal, index) => {
      if (isReal) indexes.push(index);
      return indexes;
    }, []);
  }
  return Array.isArray(segmentPanels?.points3D) ? segmentPanels.points3D.map((_, index) => index) : [];
};

const filterPanelArrayByIndexes = (value, indexes) =>
  Array.isArray(value) ? indexes.map((index) => value[index]).filter((item) => item !== undefined) : value;

const getExportPanelData = (sourcePanelData) =>
  Array.isArray(sourcePanelData)
    ? sourcePanelData.map((segmentPanels) => {
        if (!segmentPanels) return segmentPanels;
        const realPanelIndexes = getRealPanelIndexes(segmentPanels);
        return {
          ...segmentPanels,
          points: filterPanelArrayByIndexes(segmentPanels.points, realPanelIndexes),
          projectedPoints: filterPanelArrayByIndexes(segmentPanels.projectedPoints, realPanelIndexes),
          points3D: filterPanelArrayByIndexes(segmentPanels.points3D, realPanelIndexes),
          is_valid: filterPanelArrayByIndexes(segmentPanels.is_valid, realPanelIndexes),
          realPanels: realPanelIndexes.map(() => true),
          virtual_points3D: []
        };
      })
    : sourcePanelData;

const patchRaycasterIntersectObjects = (raycaster) => {
  if (!raycaster || raycaster.__raycastableOnlyIntersectObjects) return;
  const intersectObjects = raycaster.intersectObjects.bind(raycaster);
  raycaster.intersectObjects = (objects = [], recursive = true, intersects = []) =>
    intersectObjects(getRaycastableObjects(objects, recursive), false, intersects);
  raycaster.__raycastableOnlyIntersectObjects = true;
};

const isRetryableShadingRequiredDataError = (error) => {
  const status = error?.response?.status;
  const isNetworkError =
    !error?.response &&
    (error?.code === 'ECONNABORTED' ||
      error?.code === 'ERR_NETWORK' ||
      error?.message === 'Network Error' ||
      Boolean(error?.request));
  return isNetworkError || status === 408 || status === 429 || status >= 500;
};

const fetchShadingRequiredClientData = async (api, request) => {
  let lastError;
  for (let attempt = 0; attempt <= SHADING_REQUIRED_DATA_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await api.create(request);
    } catch (error) {
      lastError = error;
      if (attempt >= SHADING_REQUIRED_DATA_RETRY_DELAYS_MS.length || !isRetryableShadingRequiredDataError(error)) {
        throw error;
      }
      await sleep(SHADING_REQUIRED_DATA_RETRY_DELAYS_MS[attempt]);
    }
  }
  throw lastError;
};


const ENABLE_SERVER_SIDE_SHADING_DEBUG_DOWNLOADS = false;
const ENABLE_CLIENT_TOP_DOWN_DSM_DOWNLOAD = false;
const ENABLE_SHADING_COMPARISON_DOWNLOADS = false;

const triggerDownload = (href, filename) => {
  const downloadAnchorNode = document.createElement('a');
  downloadAnchorNode.setAttribute('href', href);
  downloadAnchorNode.setAttribute('download', filename);
  document.body.appendChild(downloadAnchorNode);
  downloadAnchorNode.click();
  downloadAnchorNode.remove();
};

const downloadDataUrlAsPng = async (dataUrl, filename) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    triggerDownload(objectUrl, filename);
  } finally {
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }
};

const dataTextureToGrayscaleBlob = (texture) => {
  const { data, width, height } = texture.image;
  const channels = data.length === width * height ? 1 : data.length / (width * height);
  const isFloat = data instanceof Float32Array || data instanceof Float64Array;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  const image = context.createImageData(width, height);

  for (let i = 0; i < width * height; i += 1) {
    const sourceValue = data[i * channels];
    const value = isFloat ? Math.round(Math.max(0, Math.min(1, sourceValue)) * 255) : sourceValue;
    const offset = i * 4;
    image.data[offset] = value;
    image.data[offset + 1] = value;
    image.data[offset + 2] = value;
    image.data[offset + 3] = 255;
  }
  context.putImageData(image, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Unable to encode Solar Access PNG.'))), 'image/png');
  });
};

const downloadSolarAccessComparisonImages = async (leadUid, source, materials) => {
  if (!ENABLE_SHADING_COMPARISON_DOWNLOADS) return;
  for (let month = 0; month <= 12; month += 1) {
    const texture = materials.getSolarAccessTexture(month)?.[0];
    if (!texture?.image?.data) continue;
    const label = month === 0 ? 'annual' : `month-${String(month).padStart(2, '0')}`;
    const blob = await dataTextureToGrayscaleBlob(texture);
    const objectUrl = URL.createObjectURL(blob);
    try {
      triggerDownload(objectUrl, `solar-access-${source}-${leadUid}-${label}.png`);
    } finally {
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
    await sleep(100);
  }
};

const downloadServerSideShadingDebugImages = async (leadUid, shadingJson) => {
  if (!ENABLE_SERVER_SIDE_SHADING_DEBUG_DOWNLOADS) return;

  const shadingData = shadingJson?.shading_data || shadingJson;
  const imageGroups = ['solarAccess', 'tsrf', 'tof', 'diffuse'];
  const downloads = [];

  imageGroups.forEach((groupName) => {
    const images = shadingData?.[groupName];
    if (!Array.isArray(images)) return;

    images.forEach((dataUrl, index) => {
      if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:image/')) {
        return;
      }

      downloads.push({
        dataUrl,
        filename: `server-side-shading-${leadUid}-${groupName}-${String(index).padStart(2, '0')}.png`
      });
    });
  });

  for (const item of downloads) {
    // Keep a small gap between downloads so browsers are less likely to drop them.
    // This is a temporary debug helper and can be disabled with the flag above.
    // eslint-disable-next-line no-await-in-loop
    await downloadDataUrlAsPng(item.dataUrl, item.filename);
    // eslint-disable-next-line no-await-in-loop
    await sleep(150);
  }
};

const Canvas3D = forwardRef(
  (
    {
      imgSrc,
      dsmSrc,
      segments,
      obstacles,
      trees,
      panels,
      annotations,
      visible,
      size,
      readonly = false,
      proposalShadings,
      iframeCallback,
      initViewMode = '2D',
      initialZoom = 3, // Initial camera zoom for 2D mode (default: 3 for backward compatibility)
      // When true or not passed: panel click in readonly is allowed (current behavior). When false: panel click only when selectionMode is 'panels'. Used by designer for homeowner View Panels.
      allowPanelToggleInReadonly = true,
      // External dependencies (previously from LeadContext)
      lead,
      designId = null,
      canvasId = null,
      user,
      displaySnack,
      saveShadingsAPI,
      runShadingsAPI,
      runShadingRequiredClientDataAPI,
      weatherDataset = 'nsrdb_goes_v4_2024',
      exportDesignAPI,
      shadingHasRun = false,
      stringingData = null,
      showStringing = false,
      forceShowStringing = false,
      stringingPanelIndexMode = 'all',
      stringingSelectedPanelIndexMap = null,
      stringingRoofIndexMap = null,
      onLoaded = null,
      hideToolbar = false, // When true, toolbar is hidden but still in DOM (refs/progress work). Designer library passes true.
      onTreeClick = null, // Optional. When provided (e.g. by designer), called with (treeIndex: number) when a 3D tree mesh is clicked. Not used by other consumers.
      allowLeadShadingFallback = true,
      // When true (e.g. homeowner shading toggle on), do not strip the shading view on accordion change — parent keeps views in sync with the bottom bar.
      keepShadingViewOnAccordionChange = false,
      previewLayers = false,
      // When true, annotation placement uses default cursor and hides placement preview sphere (designer homeowner / consultant read-only).
      annotationsNeutralPlacementCursor = false,
      // When true, fire-setback edge-type / per-state modes color roof outline
      // edges by Edit Edge Types. Setback bands stay orange in every mode.
      colorSetbacksByEdgeType = false,
      footprint = null,
      footprintPolygon = null,
      footprint_polygon = null
    },
    ref
  ) => {
    const leadId = lead?.uid || null;
    const shadingScopeId = shadingRequestScope(canvasId, designId);
    const hasExplicitProposalShadings =
      typeof proposalShadings === 'string' ? proposalShadings.trim().length > 0 : Boolean(proposalShadings);
    const explicitProposalShadings = !shadingScopeId && hasExplicitProposalShadings ? proposalShadings : null;
    const proposalShadingsMatchesActiveDesign = useMemo(() => {
      return shadingMatchesScope(proposalShadings, leadId, canvasId, designId);
    }, [canvasId, leadId, proposalShadings, shadingScopeId]);
    const activeProposalShadings = proposalShadingsMatchesActiveDesign ? proposalShadings : null;
    const resolvedProposalShadings = allowLeadShadingFallback
      ? (activeProposalShadings ?? explicitProposalShadings ?? lead?.property?.shading_images)
      : (activeProposalShadings ?? explicitProposalShadings);
    const hasDSMSource = typeof dsmSrc === 'string' ? Boolean(dsmSrc.trim()) : Boolean(dsmSrc);

    const shadingCacheKey = useMemo(
      () =>
        buildShadingCacheKey({
          designId: shadingScopeId,
          dsmSrc,
          weatherDataset,
          segments,
          obstacles,
          trees
        }),
      [shadingScopeId, dsmSrc, weatherDataset, segments, obstacles, trees]
    );
    const {
      viewerMode = false,
      resolution,
      selectionMode,
      setSelectionMode,
      selectedObjects,
      removeFromSelectedObjects,
      addToSelectedObjects,
      addToSelectedObjectsBulk,
      clearSelection,
      deleteSelectedObjects,
      setHeight,
      setPitch,
      setSetback,
      setSegmentPosition,
      changeEdgeTypes,
      moveRoofVertex,
      coordinates,
      setSegmentShadingFactors,
      setSegmentShadingFactorsServerSide,
      accordion,
      setShadingTexture,
      shadingStatus,
      shadingIsRunning,
      setShadingStatus,
      setShadingIsRunning,
      setServerSideShadingIsRunning,
      setSegmentParameters,
      optPOA,
      automaticAzimuthMode,
      mode,
      setMode,
      designType,
      updateRGBTextureIsRunning,
      setUpdateRGBTextureIsRunning,
      setAnnotations,
      designFrame,
      setTool, // 🆕 Extract setTool from DesignContext
      /** When true, do not strip `panels` from views when accordion becomes `annotations` (homeowner multi-select). */
      keepPanelsViewWithAnnotations
      // moveMode,
      // tool,
      // setViewMode,
    } = useContext(DesignContext);
    const preservePanelsWithAnnotationsAccordion = keepPanelsViewWithAnnotations === true;
    const { canvasPool } = useContext(CanvasPoolContext);
    const changeTextureByMonthRef = useRef();
    const [viewMode, setViewMode] = useState(initViewMode);
    const [viewState, setViewState] = useState();
    const didCallOnLoadedRef = useRef(false);

    const [designerCapabilities, setDesignerCapabilities] = useState(['move-panel-snap']);

    const [drawingCapabilities, setDrawingCapabilities] = useState(['magnetize', 'snap45', 'snap90', 'snap180']);
    const [editTool, setEditTool] = useState(null);

    const shadingRunRef = useRef(null);
    const sceneRef = useRef();
    const sceneObjRef = useRef();
    const canvas = useRef();
    const controls = useRef();
    const toolbar = useRef();
    const container = useRef();
    const exportRef = useRef();

    const [[isLoaded, loadingTitle, loadedComponents], setIsLoaded] = useState([false, '', []]);

    const updateLoadingStatus = (component = null, checkRef = true) => {
      if (component) loadedComponents.push(component);
      const loadedRef = !!(!checkRef || (container.current && sceneRef.current && controls.current));
      const hasAssets =
        (loadedComponents.includes('DSM.Loaded') || loadedComponents.includes('DSM.Failed')) &&
        (loadedComponents.includes('Irradiance.Loaded') || loadedComponents.includes('Irradiance.Failed')) &&
        (loadedComponents.includes('Shading.Loaded') ||
          loadedComponents.includes('Shading.Failed') ||
          loadedComponents.includes('Shading.None'));
      const loaded = hasAssets && (loadedRef || isLoaded);
      if (loaded) clearInterval(loadingTimeout);
      if (loaded && !didCallOnLoadedRef.current && typeof onLoaded === 'function') {
        didCallOnLoadedRef.current = true;
        try {
          onLoaded();
        } catch (e) {}
      }
      setIsLoaded([loaded, component, loadedComponents]);
    };

    const [views, setViews] = useState(() => {
      const initialViews = !readonly ? ['texture', 'trees', 'roofs'] : ['texture', 'trees', 'roofs', 'panels'];
      if (previewLayers) {
        if (!initialViews.includes('panels')) initialViews.push('panels');
        if (!initialViews.includes('shading')) initialViews.push('shading');
      }
      return hasDSMSource ? [...initialViews, 'dsm'] : initialViews;
    });
    const [captureStringing, setCaptureStringing] = useState(false);
    const [imagerySize, setImagerySize] = useState(null);

    const {
      orgDSMTexture,
      dsmTexture,
      minDSM,
      maxDSM,
      displacementBias,
      dsmOffset,
      dsmRaster,
      dsmRasterHeight,
      dsmRasterWidth,
      loadStatus: dsmLoadStatus,
      fillPolygon,
      getPointClouds,
      calculateAndSetOffset,
      changeOffset,
      getDSMData
    } = useDSM(canvasPool, dsmSrc, segments, trees, updateLoadingStatus, imagerySize);

    const weather = useWeatherData(coordinates, updateLoadingStatus, readonly);
    const { month, setMonth, getOptPitchAndAzimuth } = weather;

    const applyTextureBasedOnAccordionRef = useRef();

    const onShadingLoaded = useCallback(
      (json) => {
        setMonth(0);
        const segs = json?.segments;
        const hasPerSegmentFactors =
          Array.isArray(segs) && segs.length > 0 && segs[0] && typeof segs[0].solarAccess !== 'undefined';
        if (hasPerSegmentFactors) setSegmentShadingFactorsServerSide(segs);
        setTimeout(() => applyTextureBasedOnAccordionRef.current?.(), 100);
      },
      [setMonth, setSegmentShadingFactorsServerSide]
    );

    const materials = useMaterials({
      canvasPool,
      imgSrc,
      dsmTexture,
      month,
      setShadingTexture,
      sceneRef,
      fillPolygon,
      updateLoadingStatus,
      proposalShadings: resolvedProposalShadings,
      shadingCacheKey,
      iframeCallback,
      onShadingLoaded,
      // Pass external dependencies
      lead,
      designId: shadingScopeId,
      user,
      saveShadingsAPI
    });

    const {
      rgbTexture,
      setRGBTexture,
      texture,
      selectedTexture,
      setTexture,
      setCurrentTexture,
      changeMaterials,
      getMaskCanvas,
      resetRGBTexture
    } = materials;

    useEffect(() => {
      const width = rgbTexture?.image?.width;
      const height = rgbTexture?.image?.height;
      if (!width || !height) return;
      setImagerySize((current) => (current?.width === width && current?.height === height ? current : { width, height }));
    }, [rgbTexture]);

    applyTextureBasedOnAccordionRef.current = () => {
      // Bottom bar / toggleView: whenever the shading view is active, show the monthly gradient (any accordion).
      if (views.includes('shading')) {
        changeTextureByMonthRef.current?.();
      } else if (accordion === 'shading' || accordion === 'production') {
        changeTextureByMonthRef.current?.();
      } else if (rgbTexture) {
        setCurrentTexture(rgbTexture);
      }
    };

    const {
      verticesInfo: {
        orgVertices,
        vertices,
        vertices3D,
        verticesType,
        eaves,
        pitches,
        azimuths,
        eaveHeights,
        UV,
        changeClockwise,
        ranges,
        parents
      },
      scale,
      cx,
      cy,
      c,
      inchePerUnit,
      footPerUnit,
      getFootPerSceneUnit,
      getInchePerSceneUnit,
      getSceneUnitPerFoot,
      getSetbacks,
      updateStateFromSegments,
      updateEavesByAzimuth,
      magnetizeVertices,
      getBorders
    } = use3DReconstruction(segments, obstacles, texture, visible, readonly, mode);

    const roofAreas3D = useMemo(() => {
      const roofVertexIndices = (verticesType || []).map((t, idx) => (t === 'roof' ? idx : null)).filter((v) => v !== null);

      const areasSqFt = roofVertexIndices.map((vertexIdx) => {
        const poly3d = vertices3D?.[vertexIdx];
        const areaSceneUnits2 = polygonArea3D(poly3d);
        const ftPerSceneUnit = typeof getFootPerSceneUnit === 'function' ? getFootPerSceneUnit(1) : 0;
        const areaSqFt = ftPerSceneUnit > 0 ? areaSceneUnits2 * ftPerSceneUnit * ftPerSceneUnit : 0;
        return Number.isFinite(areaSqFt) ? areaSqFt : 0;
      });

      const signature = areasSqFt.map((a) => a.toFixed(3)).join(',');

      return {
        areasSqFt,
        unit: 'ft²',
        signature
      };
    }, [vertices3D, verticesType, getFootPerSceneUnit]);

    // const drawEaves = () => {
    //   const material = new THREE.LineBasicMaterial({ color: 0xff0000 });
    //   const startMaterial = new THREE.MeshBasicMaterial({ color: 'blue' });
    //   const endMaterial = new THREE.MeshBasicMaterial({ color: 'green' });
    //   eaves.forEach(([p1, p2]) => {
    //     const vs = new THREE.Vector3(p1.x, p1.z + 1, p1.y);
    //     const ve = new THREE.Vector3(p2.x, p2.z + 1, p2.y);
    //     const geometry = new THREE.BufferGeometry().setFromPoints([vs, ve]);
    //     const line = new THREE.Line(geometry, material);
    //     sceneRef.current.add(line);
    //     const startGeometry = new THREE.SphereGeometry(0.5, 15, 15);
    //     const startMarker = new THREE.Mesh(startGeometry, startMaterial);
    //     startMarker.position.set(vs.x, vs.y, vs.z);
    //     sceneRef.current.add(startMarker);
    //     const endGeometry = new THREE.SphereGeometry(0.5, 15, 15);
    //     const endMarker = new THREE.Mesh(endGeometry, endMaterial);
    //     endMarker.position.set(ve.x, ve.y, ve.z);
    //     sceneRef.current.add(endMarker);
    //   });
    // };

    // useEffect(() => {
    //   if (sceneRef.current) drawEaves();
    // }, [eaves]);

    const center = [cx / c, cy / c];

    const {
      moveStatus,
      panelData,
      panelEaves,
      setbacksData,
      setbacks3dData,
      roofTransformations,
      panelPlacement,
      panelPlacementByIndices,
      deletePanels,
      rotatePanels,
      movePanels,
      insertPanels,
      canInsertPanel,
      alignHorizontal,
      alignVertical,
      clearPanels
    } = usePanelOperation({
      segments,
      panels,
      vertices,
      vertices3D,
      orgVertices,
      verticesType,
      parents,
      eaves,
      pitches,
      azimuths,
      eaveHeights,
      changeClockwise,
      inchePerUnit,
      scale,
      center,
      getSetbacks,
      readonly,
      designerCapabilities
    });

    const { treeData } = useTreeOperation({
      trees,
      scale,
      center,
      footPerUnit
    });

    const shader = useShader({
      canvasPool,
      imgSrc,
      dsmTexture,
      minDSM,
      maxDSM,
      displacementBias,
      center,
      scale,
      coordinates,
      resolution,
      ranges,
      materials,
      weather,
      readonly
    });

    const {
      verticesToSegments,
      addShape,
      editShapes,
      mergeShapes,
      splitShapes,
      deleteShape,
      deletePointFromShape,
      addPointToShape,
      setLineLabel
    } = useDrawing(
      vertices,
      verticesType,
      segments,
      obstacles,
      trees,
      treeData,
      changeClockwise,
      getInchePerSceneUnit,
      scale,
      cx,
      cy,
      c
    );

    const updateRGBTexture = () => {
      resetRGBTexture();
      if (updateRGBTextureIsRunning === true) {
        setUpdateRGBTextureIsRunning(false);
      }
    };

    const { currentShadingType: shadingType, setShadingType } = shader;

    const changeTextureByMonth = () => {
      let shadingTextures;

      if (shadingType === 'TSRF') shadingTextures = materials.getTSRFTexture(month);
      else if (shadingType === 'TOF') shadingTextures = materials.getTOFTexture(month);
      else if (shadingType === 'Solar Access') shadingTextures = materials.getSolarAccessTexture(month);

      if (shadingTextures) {
        setCurrentTexture(shadingTextures[1], month);
      }
    };
    changeTextureByMonthRef.current = changeTextureByMonth;

    const uuidv4 = () =>
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });

    const shiftLatLong = (coordinates, v, xzy = false) => {
      const [centerLat, centerLon] = coordinates;
      const [dxCm, dyCm, dzCm] = [v.x, xzy ? v.z : v.y, xzy ? v.y : v.z];
      const degPerMeterLat = 1 / 111111;
      const degPerMeterLon = 1 / (111111 * Math.cos((centerLat * Math.PI) / 180));
      const dxMeter = dxCm / 100;
      const dyMeter = dyCm / 100;
      const newLat = centerLat - dyMeter * degPerMeterLat;
      const newLon = centerLon + dxMeter * degPerMeterLon;
      const dzFeet = dzCm * 0.0328;
      return [newLon, newLat, dzFeet];
    };

    function circleToPolygon3D(center, radius, segments = 64) {
      const [cx, cy, cz] = center;
      const pts = [];

      for (let i = 0; i < segments; i++) {
        const θ = (i / segments) * 2 * Math.PI;
        const x = cx + Math.cos(θ) * radius;
        const y = cy + Math.sin(θ) * radius;
        const z = cz;
        pts.push(new THREE.Vector3(x, y, z));
      }

      return pts;
    }

    const downloadJSON = () => {
      const { width, height } = texture.image;

      const getLatLong = (v, xyz = false) => {
        const pos = v
          .clone()
          .multiplyScalar(resolution / scale)
          .add(new THREE.Vector3(center[0] - width / 2, center[1] - height / 2, 0));
        return shiftLatLong(coordinates, pos, xyz);
      };

      const geoEncode = (poly, properties, shapeType) => {
        let coords = [poly.map((v) => getLatLong(v, false))];
        if (shapeType !== 'Point') coords[0].push(coords[0][0]);
        else coords = coords[0][0];
        if (shapeType == 'Line') shapeType = 'LineString';
        return {
          type: 'Feature',
          properties: properties,
          geometry: {
            coordinates: coords,
            type: shapeType
          }
        };
      };
      const setbacks = vertices.filter((poly) => poly !== null).map((poly, index) => getSetbacks(poly, index));

      const segmentsFeatures = vertices3D
        .filter((v, vi) => verticesType[vi] === 'roof')
        .map((poly, index) =>
          geoEncode(
            poly,
            {
              id: uuidv4(),
              index: index,
              type: 'face',
              azimuth: segments[index].azimuth,
              pitch: segments[index].pitch,
              height: segments[index].eaveHeight
            },
            'Polygon'
          )
        );

      const obstacleFeatures = vertices3D
        .filter((v, vi) => verticesType[vi] !== 'roof')
        .map((poly, index) =>
          geoEncode(
            poly,
            {
              id: uuidv4(),
              type: 'protrusions',
              index: index,
              radius: obstacles[index].radius,
              height: obstacles[index].height,
              shape: obstacles[index].shape
            },
            obstacles[index].shape
          )
        );

      const exportPanelData = getExportPanelData(panelData);
      const panelFeatures = exportPanelData
        ?.map((segmentPanels, index) => {
          return segmentPanels.points3D.map((pnl, j) => {
            return geoEncode(
              pnl,
              {
                id: uuidv4(),
                type: 'panel',
                index: index,
                azimuth: segmentPanels.azimuth,
                tilt: segmentPanels.tilt,
                orientation: segmentPanels.points[j][4] === 'h' ? 'horizontal' : 'vertical'
              },
              'Polygon'
            );
          });
        })
        .flat();

      const treeFeatures = treeData.map((tree, index) =>
        geoEncode(
          circleToPolygon3D(tree.position, tree.radius, 64),
          {
            id: uuidv4(),
            index: index,
            type: 'tree',
            radius: tree?.radius,
            height: tree?.height
          },
          'Polygon'
        )
      );

      const annotationFeatures = annotations?.map((annot, index) =>
        geoEncode(
          [new THREE.Vector3(annot.position['x'], annot.position['y'], annot.position['z'])],
          {
            id: uuidv4(),
            index: index,
            type: 'annotation',
            text: annot?.text,
            annotation_type: annot?.type
          },
          'Point'
        )
      );

      const data = {
        id: lead.property.lead,
        site: {
          address: {
            addressOne: `${lead.property.street}`,
            addressTwo: null,
            city: lead.property.city,
            province: lead.property.state,
            postalCode: lead.property.zipcode
          },
          coordinateSystemOrigin: {
            latitude: coordinates[0],
            longitude: coordinates[1]
          },
          // resolution value in feet
          resolution: resolution / 30.48
        },
        geoJSON: {
          type: 'FeatureCollection',
          features: [...segmentsFeatures, ...obstacleFeatures, ...panelFeatures, ...treeFeatures, ...annotationFeatures]
        },
        setbacks: setbacks,
        edgesType: segments.map((s) => s.lines)
      };

      if (iframeCallback) {
        if (['RIGHTA', 'AERIAL'].includes(user?.company.id)) iframeCallback('3DJson', data);
      } else {
        const dataStr = `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(data))}`;
        const downloadAnchorNode = document.createElement('a');
        downloadAnchorNode.setAttribute('href', dataStr);
        downloadAnchorNode.setAttribute('download', 'data.json');
        document.body.appendChild(downloadAnchorNode);
        downloadAnchorNode.click();
        downloadAnchorNode.remove();
      }
    };

    const downloadDXF = () => {
      const { width, height } = texture.image;
      // const json = shader.exportScene(sceneRef.current, lead.uid, {
      //   height,
      //   width,
      //   imgSrc,
      //   dsmSrc,
      //   coordinates,
      //   resolution,
      //   scale,
      //   minDSM,
      //   maxDSM,
      //   displacementBias,
      //   ranges,
      //   center,
      //   segments,
      //   obstacles,
      //   trees,
      //   treeData,
      //   panels,
      //   panelData,
      //   state: { orgVertices, vertices3D },
      // });
      // downloadObjectAsJson(JSON.stringify(json), 'scene');

      const state = {
        height,
        width,
        coordinates,
        resolution,
        scale,
        treeData,
        panelData: getExportPanelData(panelData)
      };

      const exportType = 'dxf'; // dwg
      const request = {
        uid: lead.uid,
        design_id: designId,
        format: 'autocad',
        export_type: exportType,
        state: state
      };

      exportDesignAPI
        .create(request)
        .then((response) => {
          displaySnack({
            variant: 'success',
            message: `Export to ${exportType} was executed successfully.`
          });
          const dataStr = `data:image/x-${exportType};charset=utf-8,${encodeURIComponent(response)}`;
          const downloadAnchorNode = document.createElement('a');
          downloadAnchorNode.setAttribute('href', dataStr);
          downloadAnchorNode.setAttribute('download', `model-${lead.uid}.${exportType}`);
          document.body.appendChild(downloadAnchorNode);
          downloadAnchorNode.click();
          downloadAnchorNode.remove();
        })
        .catch(() => {
          displaySnack({
            variant: 'danger',
            message: `Error in export to ${exportType}.`
          });
        });
      // const d = new Drawing();
      // d.addLayer('roofs', Drawing.ACI.YELLOW, 'DOTTED');
      // d.setActiveLayer('roofs');
      // vertices.forEach((p, pi) => {
      //   for (let i = 0; i < p.length; i += 1) {
      //     const v1 = p[i];
      //     const v2 = p[(i + 1) % p.length];
      //     const { x: x1, y: y1 } = v1;
      //     const { x: x2, y: y2 } = v2;
      //     d.drawLine(x1, y1, x2, y2);
      //   }
      // });

      // d.setUnits('Decimeters');
      // d.drawText(10, 0, 10, 0, 'Hello World'); // draw text in the default layer named "0"
      // d.addLayer('l_green', Drawing.ACI.GREEN, 'CONTINUOUS');
      // d.setActiveLayer('l_green');
      // d.drawText(20, -70, 10, 0, 'go green!');
      // d.addLayer('l_yellow', Drawing.ACI.YELLOW, 'DOTTED')
      //   .setActiveLayer('l_yellow')
      //   .drawCircle(50, -30, 25);
      // const dataStr = `data:image/x-dxf;charset=utf-8,${encodeURIComponent(d.toDxfString())}`;
      // const downloadAnchorNode = document.createElement('a');
      // downloadAnchorNode.setAttribute('href', dataStr);
      // downloadAnchorNode.setAttribute('download', `model-${lead.uid}.dxf`);
      // document.body.appendChild(downloadAnchorNode);
      // downloadAnchorNode.click();
      // downloadAnchorNode.remove();
    };

    const getSimilarVertex = (pindex, vindex) => {
      const selected = [];
      const { x, y } = vertices3D[pindex][vindex];
      vertices3D.forEach((poly, pi) => {
        if (verticesType[pi] === 'roof') {
          poly.forEach((v, vi) => {
            if (v.x === x && v.y === y) selected.push(`${pi}-${vi}`);
          });
        }
      });
      return selected;
    };

    const addSelectedPoint = (pindex, vindex) => {
      const selected = getSimilarVertex(pindex, vindex);
      addToSelectedObjects(selected, 'roofVertices');
    };

    const removeSelectedPoint = (pindex, vindex) => {
      const selected = getSimilarVertex(pindex, vindex);
      removeFromSelectedObjects(selected, 'roofVertices');
    };

    const toggleView = (view, forceRemove = false, forceAdd = false) => {
      if (!hasDSMSource && ['dsm', 'dsmhd', 'dsmhouse'].includes(view)) return;
      console.log('🎯 Canvas3D - toggleView called:', {
        view,
        forceRemove,
        forceAdd,
        currentViews: [...views],
        panelSelection
      });

      const viewNames = [
        'sun',
        'texture',
        'trees',
        'setbacks',
        'panels',
        'roofs',
        'shading',
        'dsm',
        'dsmhd',
        'dsmhouse',
        'move',
        'insert',
        'virtualPanels',
        'measurements'
      ];
      const removeView = (vw) => {
        const idx = views.indexOf(vw);
        if (idx >= 0) views.splice(idx, 1);
      };

      if ((views.includes(view) || forceRemove) && !forceAdd) {
        removeView(view);
        console.log('🎯 Canvas3D - toggleView removing view:', view, 'new views:', [...views]);
        switch (view) {
          case 'shading':
            setCurrentTexture(rgbTexture, month);
            // materials.changeMaterials(sceneRef.current);
            break;
          case 'dsm':
            break;
          default:
            break;
        }
      } else {
        if (!views.includes(view)) views.push(view);
        console.log('🎯 Canvas3D - toggleView adding view:', view, 'new views:', [...views]);
        switch (view) {
          case 'move':
            removeView('insert');
            removeView('virtualPanels');
            break;
          case 'insert':
            removeView('move');
            removeView('virtualPanels');
            break;
          case 'virtualPanels':
            removeView('move');
            removeView('insert');
            break;
          case 'shading':
            changeTextureByMonth();
            break;
          case 'dsm':
            break;
          default:
            break;
        }
      }

      setViews([...views]);
    };

    const runOptimizer = (selectAll = false, optimizePosition = true) => {
      if (readonly) return;

      const oldState = {
        orgVertices,
        vertices,
        vertices3D,
        verticesType,
        eaves,
        pitches,
        azimuths,
        eaveHeights,
        UV,
        changeClockwise,
        ranges,
        parents
      };
      const width =
        texture?.image?.width ??
        rgbTexture?.image?.width ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterWidth ??
        1024;
      const height =
        texture?.image?.height ??
        rgbTexture?.image?.height ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterHeight ??
        1024;
      initState(oldState, segments, scale, resolution, height, width);

      // BEGIN for export to express.js api
      // const state = {
      //   scale, center, ranges, treeData, resolution, orgVertices, vertices, vertices3D, verticesType, eaves, pitches, azimuths, eaveHeights, UV, changeClockwise, parents,
      // };
      // const optimalPOA = weather.getOptPitchAndAzimuth();
      // const dsmParams = { minDSM, maxDSM, dsmOffset, displacementBias, dsmSrc };
      // const [positions, intensities, irradiances] = shader.exportSunPositionAndIntensity();
      // const data = { positions, intensities, irradiances, dsmParams, optimalPOA, treeData, state };
      // shader.downloadContent(JSON.stringify(data), "irradiance.json", "application/json");
      // END for export to express.js api

      let planesPoints = null;
      if (dsmLoadStatus === 'DSM.Loaded' && dsmRaster) {
        const indicesMask = segmentIndexMask(canvasPool, oldState, segments, dsmRasterHeight, dsmRasterWidth);
        const baseDSM = minDSM + (displacementBias / 255) * (maxDSM - minDSM);
        planesPoints = getDSMPoints(
          segments,
          trees,
          oldState,
          center,
          scale,
          resolution,
          dsmRaster,
          baseDSM,
          dsmOffset,
          indicesMask
        );
      }

      optimizePlanes(segments, oldState, resolution, scale, undefined, undefined, planesPoints); // const allVertices =
      // const color = THREE.MathUtils.randInt(0, 0xffffff);
      // allVertices.forEach(([x, y, z]) => {
      //   const point = new THREE.Vector3(x, y, z);
      //   const startMarkerGeometry = new THREE.SphereGeometry(0.5, 15, 15);
      //   const startMarkerMaterial = new THREE.MeshBasicMaterial({ color });
      //   const marker = new THREE.Mesh(startMarkerGeometry, startMarkerMaterial);
      //   marker.position.set(point.x, point.z, point.y);
      //   sceneRef.current.add(marker);
      // });
      setSegmentParameters(segments);

      // const selectedRoof = selectAll ? segments.map((s, si) => si) : selectedObjects.roofSegments;
      // const selectedVertices = selectAll ? segments.map((s) => s.geometry.map((v, vi) => vi)) : selectedObjects.roofVertices;
      // // const eavesIndex = getEaveIndices(oldState);

      // // const indices = optimizeVertexPosition(segments, oldState, selectedRoof, selectedVertices, optimizePosition);
      // // indices.forEach((stack) => {
      // //   stack.forEach((points) => {
      // //     const color = THREE.MathUtils.randInt(0, 0xffffff);
      // //     points.forEach(([pi, vi]) => {
      // //       const point = vertices3D[pi][vi];
      // //       const startMarkerGeometry = new THREE.SphereGeometry(0.5, 15, 15);
      // //       const startMarkerMaterial = new THREE.MeshBasicMaterial({ color });
      // //       const marker = new THREE.Mesh(startMarkerGeometry, startMarkerMaterial);
      // //       marker.position.set(point.x, point.z, point.y);
      // //       sceneRef.current.add(marker);
      // //     });
      // //   });
      // // });
      // // return;

      // const [newOrgVertices, roofAttributes] = optimizeVertexPosition(segments, oldState, resolution, scale, selectedRoof, selectedVertices, optimizePosition);
      // const newSegmentsPosition = verticesToSegments(newOrgVertices);
      // setSegmentPosition(newSegmentsPosition, roofAttributes);
      if (iframeCallback) iframeCallback('Optimize', '');
    };

    const edgeDetection = (depthForEdgeDetection, segmentIndices = []) => {
      if (readonly) return;
      const oldState = {
        orgVertices,
        vertices,
        vertices3D,
        verticesType,
        eaves,
        pitches,
        azimuths,
        eaveHeights,
        UV,
        changeClockwise,
        ranges,
        parents
      };
      const width =
        texture?.image?.width ??
        rgbTexture?.image?.width ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterWidth ??
        0;
      const height =
        texture?.image?.height ??
        rgbTexture?.image?.height ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterHeight ??
        0;
      initState(oldState, segments, scale, resolution, height, width);

      const roofVertices = orgVertices.filter((p, pi) => verticesType[pi] === 'roof');
      const borders = getBorders(oldState, roofVertices, true);

      magnetizeVertices(segments);

      if (dsmLoadStatus === 'DSM.Loaded' && depthForEdgeDetection) {
        const offset = calculateAndSetOffset(borders, resolution, scale, center);
        const indicesMask = segmentIndexMask(canvasPool, oldState, segments, height, width);
        const baseDSM = minDSM + (displacementBias / 255) * (maxDSM - minDSM);
        detectEdgeTypes(
          segments,
          oldState,
          center,
          scale,
          resolution,
          updateStateFromSegments,
          getBorders,
          true,
          dsmRaster,
          baseDSM,
          offset,
          indicesMask
        );
      } else {
        detectEdgeTypes(segments, oldState, center, scale, resolution, updateStateFromSegments, getBorders, false);
      }

      changeEdgeTypes(segments);
      if (iframeCallback) iframeCallback('EdgeLabeling', '');
    };

    const setIndicesMask = () => {
      const state = {
        orgVertices,
        vertices,
        vertices3D,
        verticesType,
        eaves,
        pitches,
        azimuths,
        eaveHeights,
        UV,
        changeClockwise,
        ranges,
        parents
      };
      const { width, height } = texture.image;
      segmentIndexMask(canvasPool, state, segments, height, width);
    };

    const calculateRoofShading = async (data, diffuseData) => {
      const { width, height } = texture.image;
      // segmentIndexMask(canvasPool, state, segments, height, width);
      shader.calculateAverageTextures(materials, shader, data, width, height, segments.length);
      await downloadSolarAccessComparisonImages(lead.uid, 'client', materials);

      const shadingFactors = await shader.calculateShadingFactor(data, segments.length, width);

      setSegmentShadingFactors(shadingFactors);
      toolbar.current?.progress(100); // Optional chaining for hidden toolbar
      setMonth(0); // Reset to Year view
      await materials.saveTextures({
        ranges,
        segments: segments.map((s) => ({ geometry: s.geometry }))
      }, data, diffuseData, { width, height });

      if (iframeCallback) iframeCallback('Shading', '');
    };

    const loadShading = (shadingName, onLoaded = null) => {
      const src = normalizeShadingMediaUrl(shadingName);
      if (!src) return Promise.resolve(null);
      return materials.loadShadingJson(src, (json) => {
        setMonth(0);
        setTimeout(() => {
          changeTextureByMonth();
        }, 500);
        if (Array.isArray(json?.segments)) {
          setSegmentShadingFactorsServerSide(json.segments);
        }
        if (onLoaded) onLoaded(json);
      });
    };

    const applyServerSideShadingProgress = async () => {
      for (let m = 1; m <= 12; m += 1) {
        setMonth(m);

        let shadingTextures;
        if (shadingType === 'TSRF') shadingTextures = materials.getTSRFTexture(m);
        else if (shadingType === 'TOF') shadingTextures = materials.getTOFTexture(m);
        else if (shadingType === 'Solar Access') shadingTextures = materials.getSolarAccessTexture(m);

        if (shadingTextures) setCurrentTexture(shadingTextures[1], m);
        toolbar.current?.progress((m / 13) * 100);
        await sleep(200);
      }

      setMonth(0);
      let yearTexture;
      if (shadingType === 'TSRF') yearTexture = materials.getTSRFTexture(0);
      else if (shadingType === 'TOF') yearTexture = materials.getTOFTexture(0);
      else if (shadingType === 'Solar Access') yearTexture = materials.getSolarAccessTexture(0);
      if (yearTexture) setCurrentTexture(yearTexture[1], 0);
      toolbar.current?.progress(100);
    };

    const startServerSideShading = async () => {
      if (readonly) return;
      console.log('Start server-side shading');

      setServerSideShadingIsRunning(true);

      materials.resetTSRFTextures();
      materials.resetTOFTextures();
      materials.resetSolarAccessTextures();
      changeTextureByMonth();

      const { width, height } = texture.image;
      const json = shader.exportScene(sceneRef.current, lead.uid, {
        height,
        width,
        imgSrc,
        dsmSrc,
        coordinates,
        resolution,
        scale,
        minDSM,
        maxDSM,
        displacementBias,
        ranges,
        center,
        segments,
        obstacles,
        trees,
        panels,
        state: { orgVertices }
      });
      // downloadObjectAsJson(JSON.stringify(json), 'scene');

      const request = {
        uid: lead.uid,
        dataset: weatherDataset,
        design_id: shadingScopeId
        // scene: JSON.stringify(json),
      };

      try {
        if (ENABLE_CLIENT_TOP_DOWN_DSM_DOWNLOAD) {
          await shader.downloadTopDownHeightMap(sceneRef.current, `client-top-down-dsm-${lead.uid}.png`);
        }
        if (ENABLE_SHADING_COMPARISON_DOWNLOADS) {
          setIndicesMask();
          await shader.downloadShadingIdentityMask(
            sceneRef.current,
            `shading-identity-server-${lead.uid}.png`,
            segments,
            obstacles,
            trees
          );
        }
        const response = await runShadingsAPI.create(request);
        const loadedShading = await loadShading(response, (loadedShading) => {
          if (loadedShading) {
            downloadServerSideShadingDebugImages(lead.uid, loadedShading).catch(() => {});
          }
        });

        if (!loadedShading) {
          throw new Error('Server-side shading output was not available.');
        }

        await downloadSolarAccessComparisonImages(lead.uid, 'server', materials);
        await applyServerSideShadingProgress();

        if (!iframeCallback) {
          displaySnack({
            variant: 'success',
            message: 'Server-side shading was executed successfully.'
          });
        }
        if (setShadingStatus) setShadingStatus(0);
        if (iframeCallback) iframeCallback('Shading', '');
        return true;
      } catch (e) {
        if (!iframeCallback)
          displaySnack({
            variant: 'danger',
            message: 'Error in running Server-side shading.'
          });
        if (setShadingStatus) {
          let status = shadingStatus;
          if (status >= 0) status = -1;
          setShadingStatus(status - 1);
        }
        return false;
      } finally {
        setServerSideShadingIsRunning(false);
      }
    };

    const startShading = async (m = 0, mask = null, receive_data_from_server = true, hasRetried = false) => {
      console.log('Start client-side shading month:', m);
      if (readonly) return;

      const width =
        texture?.image?.width ??
        rgbTexture?.image?.width ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterWidth ??
        1024;
      const height =
        texture?.image?.height ??
        rgbTexture?.image?.height ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterHeight ??
        1024;
      let useServerSideData = receive_data_from_server;

      if (!useServerSideData && weather.loadStatus !== 'Irradiance.Loaded') {
        displaySnack({
          variant: 'danger',
          message: 'Irradiance data was not loaded.'
        });
        return false;
      }

      const isRootRun = m === 0;
      if (isRootRun && shadingRunRef.current) return false;
      if (isRootRun) {
        shadingRunRef.current = { storage: {} };
        setShadingIsRunning(true);
      }
      const run = shadingRunRef.current;
      const finishShadingTextureJob = isRootRun ? materials.beginShadingTextureJob() : null;

      try {
        if (m === 0) {
          if (useServerSideData) {
            const request = {
              uid: lead.uid,
              dataset: weatherDataset,
              design_id: shadingScopeId,
              pitches: segments.map((s) => String(Number(s?.pitch) || 0)).join(','),
              azimuths: segments.map((s) => String(Number(s?.azimuth) || 0)).join(','),
              cache_key: shadingCacheKey || undefined
            };
            try {
              const response = await fetchShadingRequiredClientData(runShadingRequiredClientDataAPI, request);
              const validationError = getShadingRequiredClientDataValidationError(response, segments.length);

              if (validationError) {
                console.warn('Invalid shading_required_client_data response.', {
                  leadUid: lead.uid,
                  validationError,
                  currentSegmentCount: segments.length,
                  responseSegmentCount: Array.isArray(response?.tof_data) ? response.tof_data.length : null
                });
                weather.setServerSideWeatherData(null);
                throw new Error(`Invalid shading required client data: ${validationError}`);
              } else {
                weather.setServerSideWeatherData(response);
              }
            } catch (e) {
              if (!iframeCallback)
                displaySnack({
                  variant: 'danger',
                  message: 'Error in receive required shading data.'
                });
              if (iframeCallback) console.log('Error in receive required shading data.');
              throw e;
            }
          }

          run.storage = {};

          materials.resetTSRFTextures();
          materials.resetTOFTextures();
          materials.resetSolarAccessTextures();
          changeTextureByMonth();

          shader.resetRenderer();
          [run.diffuseTSRFIso, run.diffuseTSRFHorizon] = shader.shadeDiffuse(sceneRef.current, 'TSRF');
          const diffuseTSRFTexture_iso = shader.dataToTexture(run.diffuseTSRFIso, width, height);
          const diffuseTSRFTexture_horizon = shader.dataToTexture(run.diffuseTSRFHorizon, width, height);

          [run.diffuseTOFIso, run.diffuseTOFHorizon] = shader.shadeDiffuse(sceneRef.current, 'TOF');
          const diffuseTOFTexture_iso = shader.dataToTexture(run.diffuseTOFIso, width, height);
          const diffuseTOFTexture_horizon = shader.dataToTexture(run.diffuseTOFHorizon, width, height);

          materials.setDiffuseTextures(
            diffuseTSRFTexture_iso,
            diffuseTOFTexture_iso,
            diffuseTSRFTexture_horizon,
            diffuseTOFTexture_horizon
          );
          mask = getMaskCanvas(segments, width, height);

          setIndicesMask();
          if (ENABLE_SHADING_COMPARISON_DOWNLOADS) {
            await shader.downloadShadingIdentityMask(
              sceneRef.current,
              `shading-identity-client-${lead.uid}.png`,
              segments,
              obstacles,
              trees
            );
          }
          shader.captureShadingRun(width, height, segments.length);
        }

        shader.setByServerSideData(useServerSideData);

        if (shader && m <= 12 && (m === 0 || !toolbar.current?.isCanceledShading())) {
          if (m > 0) {
            const TSRFShadeData = shader.shade(sceneRef.current, 'TSRF', m);
            const TOFShadeData = shader.shade(sceneRef.current, 'TOF', m);

            const [tsrfFloatData, tofFloatData, saFloatData, sumSolarAcces] = shader.calculateSolarAccessData(
              TSRFShadeData,
              TOFShadeData,
              run.diffuseTSRFIso,
              run.diffuseTOFIso,
              run.diffuseTSRFHorizon,
              run.diffuseTOFHorizon,
              m,
              width,
              height,
              pitches,
              segments.length
            );

            run.storage[m] = [tsrfFloatData, tofFloatData, saFloatData];

            const TSRFTexture = shader.dataToTexture(tsrfFloatData, width, height, true);
            const TSRFGradientData = materials.gradiantData(tsrfFloatData, width, height, shader.ranges, true);
            const TSRFGradientTexture = shader.dataToTexture(TSRFGradientData, width, height, false);

            const TOFTexture = shader.dataToTexture(tofFloatData, width, height, true);
            const TOFGradientData = materials.gradiantData(tofFloatData, width, height, shader.ranges, true);
            const TOFGradientTexture = shader.dataToTexture(TOFGradientData, width, height, false);

            const SATexture = shader.dataToTexture(saFloatData, width, height, true);
            const SAGradientData = materials.gradiantData(saFloatData, width, height, shader.ranges, true);
            const SAGradientTexture = shader.dataToTexture(SAGradientData, width, height, false);

            materials.setTSRFTextures(m, TSRFTexture, TSRFGradientTexture);
            materials.setTOFTextures(m, TOFTexture, TOFGradientTexture);
            materials.setSolarAccessTextures(m, SATexture, SAGradientTexture);

            if (shadingType === 'TSRF') setCurrentTexture(TSRFGradientTexture, m);
            else if (shadingType === 'TOF') setCurrentTexture(TOFGradientTexture, m);
            else if (shadingType === 'Solar Access') setCurrentTexture(SAGradientTexture, m);

            if (!toolbar.current?.isCanceledShading()) toolbar.current?.progress((m / 13) * 100);
          } else {
            toolbar.current?.progress(1);
          }

          if (m === 0) {
            materials.resetTSRFTextures();
            materials.resetTOFTextures();
            materials.resetSolarAccessTextures();
            changeTextureByMonth();
          }

          if (m < 12) {
            setMonth(m + 1);
            await sleep(200);
            return await startShading(m + 1, mask, useServerSideData, hasRetried);
          }
          if (m === 12) {
            await calculateRoofShading(run.storage, [run.diffuseTSRFIso, run.diffuseTOFIso, run.diffuseTSRFHorizon, run.diffuseTOFHorizon]);
          }
        }

        return true;
      } catch (e) {
        if (!isRootRun) throw e;
        if (toolbar.current?.isCanceledShading()) return false;
        if (e.code === 'INVALID_SHADING_SAMPLES' && !hasRetried) {
          shader.finishShadingRun();
          shadingRunRef.current = null;
          return await startShading(0, null, useServerSideData, true);
        }
        console.error('Client-side shading failed', e);
        if (iframeCallback) iframeCallback('Error', { code: 'SHADING_CALCULATION_FAILED', message: 'Unable to calculate shading. Please try again.' });
        if (!iframeCallback) {
          displaySnack({
            variant: 'danger',
            message: 'Error in running client-side shading.'
          });
        }
        return false;
      } finally {
        if (finishShadingTextureJob) finishShadingTextureJob();
        if (isRootRun) {
          shader.finishShadingRun();
          shadingRunRef.current = null;
          setShadingIsRunning(false);
          console.log('end client-side shading');
        }
      }
    };

    const measure = async (callback) => {
      // alert('start measure');
      const oldState = {
        orgVertices,
        vertices,
        vertices3D,
        verticesType,
        eaves,
        pitches,
        azimuths,
        eaveHeights,
        UV,
        changeClockwise,
        ranges,
        parents
      };
      // let tryCount = 50;
      // while (!texture.image && --tryCount > 0) {
      //   await new Promise((r) => setTimeout(r, 200));
      // }
      const width =
        texture?.image?.width ??
        rgbTexture?.image?.width ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterWidth ??
        1024;
      const height =
        texture?.image?.height ??
        rgbTexture?.image?.height ??
        (typeof size === 'number' && size > 0 ? size : null) ??
        dsmRasterHeight ??
        1024;
      // alert(`${scale}, ${resolution}, ${height}, ${width}`);
      initState(oldState, segments, scale, resolution, height, width);
      // alert('measure initState');
      if (dsmRaster) {
        const dsmData = getDSMData();
        const offset = measureByDSM(canvasPool, oldState, segments, trees, dsmData);

        // points.forEach((pnts, ind) => {
        //   if (ind !== 4) return;
        //   const color = THREE.MathUtils.randInt(0, 0xffffff);
        //   pnts.forEach((point) => {
        //     const startMarkerGeometry = new THREE.SphereGeometry(0.5, 15, 15);
        //     const startMarkerMaterial = new THREE.MeshBasicMaterial({ color });
        //     const marker = new THREE.Mesh(startMarkerGeometry, startMarkerMaterial);
        //     marker.position.set(point[0], point[2], point[1]);
        //     sceneRef.current.add(marker);
        //   });
        // });
        // setSegmentParameters(segments);
        // return;

        changeOffset(offset);
      }
      setSegmentParameters(segments);
      if (callback) callback();
      if (iframeCallback) iframeCallback('Measure', '');
    };

    const updateLoadingStatusRef = useRef(updateLoadingStatus);
    updateLoadingStatusRef.current = updateLoadingStatus;

    useEffect(() => {
      if (isLoaded) {
        clearInterval(loadingTimeout);
        loadingTimeout = null;
        return undefined;
      }
      clearInterval(loadingTimeout);
      loadingTimeout = setInterval(() => updateLoadingStatusRef.current(null, false), 1000);
      return () => clearInterval(loadingTimeout);
    }, [isLoaded]);

    useEffect(() => {
      let timeoutId;
      if (views.includes('shading') && !toolbar.current?.isRunningShading() && materials) {
        timeoutId = setTimeout(() => changeTextureByMonth(), 100);
      }
      return () => {
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [month, shadingType, isLoaded, accordion, views]);

    useEffect(() => {
      if (!readonly && !dsmRaster && designType === 'M') automaticAzimuthMode(true);
      else automaticAzimuthMode(false);
    }, [automaticAzimuthMode, designType, dsmRaster, isLoaded, readonly]);

    useEffect(() => {
      if (hasDSMSource) return;
      const nextViews = views.filter((view) => !['dsm', 'dsmhd', 'dsmhouse'].includes(view));
      if (nextViews.length !== views.length) setViews(nextViews);
    }, [hasDSMSource, views]);

    useEffect(() => {
      if (isLoaded && iframeCallback) {
        iframeCallback('Loaded', '');
      }
    }, [isLoaded]);

    const segmentSelection = selectionMode === 'segment';
    const pointSelection = selectionMode === 'point';
    const lineSelection = selectionMode === 'line';
    const panelSelection = selectionMode === 'panels';
    const setbackSelection = selectionMode === 'setbacks';

    useEffect(() => {
      console.log('🎯 Canvas3D - Accordion changed useEffect:', {
        accordion,
        currentSelectionMode: selectionMode,
        hasSetSelectionMode: !!setSelectionMode
      });

      if (accordion) {
        // Strip panels when switching accordion unless homeowner/consultant bottom bar still has "View panels" on
        // while primary accordion is annotations — otherwise annotations mode removes the panels layer and panels
        // stay hidden until another feature (e.g. fire setbacks) re-adds them.
        const stripPanelsOnAccordionChange = !(accordion === 'annotations' && preservePanelsWithAnnotationsAccordion);
        if (views.includes('panels') && stripPanelsOnAccordionChange && !previewLayers) {
          toggleView('panels');
        }
        if (
          views.includes('shading') &&
          accordion !== 'annotations' &&
          !keepShadingViewOnAccordionChange &&
          !previewLayers
        ) {
          toggleView('shading');
        }
        if (views.includes('setbacks')) toggleView('setbacks');
        if (views.includes('insert')) toggleView('insert');
        if (views.includes('virtualPanels')) toggleView('virtualPanels');
      }
      setViewState('');
      switch (accordion) {
        case 'segments':
          setMode(mode === 'label' ? 'label' : 'select');
          console.log('🎯 Canvas3D - Setting selectionMode to segment');
          setSelectionMode('segment');
          break;
        case 'obstacles':
          break;
        case 'setback':
          console.log('🎯 Canvas3D - Setting selectionMode to setbacks');
          setSelectionMode('setbacks');
          if (!views.includes('setbacks')) toggleView('setbacks');
          break;
        case 'shading':
          if (!views.includes('shading')) toggleView('shading');
          break;
        case 'design':
          setSelectionMode('segment');
          if (!views.includes('panels')) toggleView('panels');
          if (!views.includes('setbacks')) toggleView('setbacks');
          break;
        case 'production':
          setSelectionMode('panels');
          if (!views.includes('panels')) toggleView('panels');
          if (!views.includes('shading')) toggleView('shading');
          if (views.includes('move')) toggleView('move', true);
          break;
        case 'trees':
          break;
        case 'revise':
          console.log('🎯 Canvas3D - Accordion is revise, setting selectionMode to panels');
          console.log('🔍 Canvas3D - Before setSelectionMode:', {
            currentSelectionMode: selectionMode,
            hasSetSelectionMode: !!setSelectionMode
          });
          setSelectionMode('panels');
          console.log('✅ Canvas3D - setSelectionMode("panels") called');
          if (!views.includes('panels')) toggleView('panels');
          if (!views.includes('setbacks')) toggleView('setbacks');
          if (!views.includes('move')) toggleView('move');
          break;
        case 'annotations':
          setViewState('annotations');
          if (!iframeCallback) setMode('');
          break;
        default:
          break;
      }
      applyTextureBasedOnAccordionRef.current?.();
    }, [accordion, keepShadingViewOnAccordionChange, preservePanelsWithAnnotationsAccordion]);

    const clearAllSelection = (forceClear = false, allObjects = false) => {
      if (!clearSelection) return;
      if (forceClear) {
        clearSelection('roofSegments', true);
        return;
      }
      if (allObjects || segmentSelection) clearSelection('roofSegments');
      if (allObjects || pointSelection) clearSelection('roofVertices');
      if (allObjects || lineSelection) clearSelection('roofLines');
      if (allObjects || setbackSelection) clearSelection('fireSetbacks');
      if (allObjects || panelSelection) clearSelection('panels');
    };

    const showSunLight = views.includes('sun') && !toolbar.current?.isRunningShading();
    const showGround = views.includes('texture');
    const showTrees = views.includes('trees');
    const showSetbacks = views.includes('setbacks');
    const showPanels = views.includes('panels');
    const showRoofs = views.includes('roofs');
    const showShading = views.includes('shading');
    const showDSM = hasDSMSource && views.includes('dsm');
    const showDSMHouse = hasDSMSource && views.includes('dsmhouse');
    const showDSMHD = hasDSMSource && views.includes('dsmhd');
    const showMeasurements = views.includes('measurements');
    const showAnnotation = views.includes('annotations');
    const moveSelection = panelSelection && views.includes('move');
    const insertObject = panelSelection && views.includes('insert');
    const showVirtualPanels = panelSelection && views.includes('virtualPanels');

    useEffect(() => {
      setTimeout(() => {
        if (texture) texture.needsUpdate = true;
        if (dsmTexture) dsmTexture.needsUpdate = true;
        materials.changeMaterials(sceneRef.current);
      }, 100);
    }, [showSunLight, showDSM, showShading, texture, dsmTexture, materials]);

    useEffect(() => {
      if (!canvas.current) return;
      switch (mode) {
        case 'select':
          canvas.current.style.cursor = 'default';
          break;
        case 'draw':
          canvas.current.style.cursor = 'crosshair';
          break;
        case 'edit':
          canvas.current.style.cursor = 'grab';
          break;
        case 'delete':
        case 'label':
          canvas.current.style.cursor = 'pointer';
          break;
        default:
          break;
      }
    }, [mode, canvas]);

    useEffect(() => {
      if (accordion === 'obstacles' || accordion === 'trees') setDrawingCapabilities([]);
      else if (accordion === 'segments') setDrawingCapabilities(['magnetize', 'snap45', 'snap90', 'snap180']);

      if (accordion === 'revise') {
        setDesignerCapabilities(['move-panel-snap']);
        clearSelection('roofSegments', true);
        clearSelection('fireSetbacks', true);
        clearSelection('panels', true);
      }
    }, [accordion, clearSelection]);

    useEffect(() => {
      if (viewMode === '3D' && ['draw', 'edit', 'delete', 'label'].includes(mode)) setViewMode('2D');
    }, [mode, setViewMode]);

    useEffect(() => {
      if (viewMode === '3D' && ['draw', 'edit', 'delete', 'label'].includes(mode)) setMode('select');
    }, [viewMode, setMode]);

    useEffect(() => {
      if (mode !== 'edit' && editTool) setEditTool(null);
    }, [mode, editTool]);

    useEffect(() => {
      if (readonly && !iframeCallback) toggleView('shading');
    }, []);

    const labels = useMemo(
      () =>
        segments
          .map((s) => s.lines)
          .map((ls, si) => {
            const lc = ls.length;
            const el = !changeClockwise[si]
              ? ls.map((l, li) => ls[(li - 1 + lc) % lc])
              : ls.map((l, li) => ls[(li + 1) % lc]).reverse();
            return el;
          }),
      [segments, changeClockwise]
    );

    const { width: gwidth, height: gheight } =
      texture?.image ??
      rgbTexture?.image ??
      (imgSrc ? { width: size || 1024, height: size || 1024 } : { width: 1, height: 1 });
    const rgbImageWidth = rgbTexture?.image?.width ?? (imgSrc ? size || 1024 : 1);
    const rgbImageHeight = rgbTexture?.image?.height ?? (imgSrc ? size || 1024 : 1);
    const imageSizeScale = Math.max(1, rgbImageWidth / 1024, rgbImageHeight / 1024);

    // useLayoutEffect(() => {
    //   if (isLoaded) {
    //     container.current.style.display = visible ? 'block' : 'none';
    //   }
    // });

    const getAnualPanelsShadings = async (options = {}) =>
      await materials.getAnualPanelsShadings(segments, panels, gwidth, gheight, {
        ...options,
        panelData,
        panelScale: scale,
        panelCenter: center
      });
    const exportToJPG = (zoom = null, lookPos = null) => exportRef.current.exportToJPG(zoom, lookPos);
    const exportThumbnail = () => exportRef.current.exportThumbnail();
    const exportDesignSnapshot = () =>
      new Promise((resolve) => {
        const previousViews = [...views];
        const yearTexture = materials.getTSRFTexture?.(0);
        const nextViews = [...views];
        if (!nextViews.includes('panels')) nextViews.push('panels');
        if (yearTexture && !nextViews.includes('shading')) nextViews.push('shading');
        if (yearTexture) setCurrentTexture(yearTexture[1], 0);
        setViews(nextViews);
        setCaptureStringing(true);
        window.setTimeout(async () => {
          let shot = null;
          try {
            shot = await exportRef.current?.exportThumbnail?.();
          } catch (error) {
            shot = null;
          }
          setCaptureStringing(false);
          setViews(previousViews);
          if (previousViews.includes('shading')) changeTextureByMonth();
          else if (rgbTexture) setCurrentTexture(rgbTexture, month);
          resolve(shot);
        }, 200);
      });

    const zoomCamera = (zoomFactor) => {
      if (sceneObjRef.current) {
        const camera = sceneObjRef.current.getCamera();
        if (viewMode === '3D') {
          if (zoomFactor !== 0) {
            camera.position.x *= 1 / zoomFactor;
            camera.position.y *= 1 / zoomFactor;
            camera.position.z *= 1 / zoomFactor;
          } else {
            camera.position.x = 0;
            camera.position.y = 100;
            camera.position.z = 200;
          }
          camera.updateProjectionMatrix();
        } else {
          if (zoomFactor !== 0) camera.zoom *= zoomFactor;
          else camera.zoom = camera.userData?.baseZoom ?? initialZoom;
          camera.updateProjectionMatrix();
        }
      }
    };

    const switchAnnotation = (newViewState = null) => {
      if (newViewState != null) setViewState(newViewState);
      else {
        if (viewState == 'annotations') setViewState('');
        else setViewState('annotations');
      }
    };

    // const setAnnotations = (annots) => {
    //   setAnnots(annots);
    // };

    const getAnnotations = () => {
      return annotations;
    };

    useImperativeHandle(ref, () => ({
      isLoaded,
      viewState,
      annotations,
      panelData,
      panelPlacement,
      panelPlacementByIndices,
      deletePanels,
      rotatePanels,
      movePanels,
      insertPanels,
      alignHorizontal,
      alignVertical,
      toggleView,
      getViews: () => [...views],
      hasDSMSource: () => hasDSMSource,
      startShading,
      updateRGBTexture,
      startServerSideShading,
      setMonth, // 🆕 Expose month setter for shading UI
      setShadingType, // 🆕 Expose shading type setter for shading UI
      runOptimizer,
      edgeDetection,
      measure,
      downloadJSON,
      downloadDXF,
      clearPanels,
      getOptPitchAndAzimuth,
      getAnualPanelsShadings,
      changeOffset,
      exportToJPG,
      exportThumbnail,
      exportDesignSnapshot,
      zoomCamera,
      setViewMode,
      getCameraAzimuth: () => {
        if (viewMode !== '3D' || !controls.current) return 0;
        const rad = controls.current.getAzimuthalAngle();
        return rad != null ? (rad * 180) / Math.PI : 0;
      },
      switchAnnotation,
      setAnnotations,
      getAnnotations,
      // 🆕 From develop: Stringing-related methods
      getRoofAreas3D: () => roofAreas3D,
      setSelectedPanelsByVertex: (selectedByVertex = {}) => {
        try {
          clearSelection('panels', true);
          const ids = [];
          Object.keys(selectedByVertex || {}).forEach((k) => {
            const vertexIdx = Number(k);
            const panelIdxs = Array.isArray(selectedByVertex[k]) ? selectedByVertex[k] : [];
            panelIdxs.forEach((pi) => {
              ids.push(`${vertexIdx}-${pi}`);
            });
          });
          if (ids.length === 0) return;
          if (typeof addToSelectedObjectsBulk === 'function') {
            addToSelectedObjectsBulk(ids, 'panels');
          } else {
            addToSelectedObjects(ids, 'panels');
          }
        } catch (e) {}
      },
      selectPanelsFromStringing: (strings = [], panelIndexMode = 'all') => {
        try {
          if (!Array.isArray(strings) || strings.length === 0) return;

          const roofVertexIndices = (verticesType || [])
            .map((t, idx) => (t === 'roof' ? idx : null))
            .filter((v) => v !== null);

          const selectionSnapshot = selectedObjects?.panels || {};
          const selectedSortedCache = {};

          clearSelection('panels', true);
          const ids = [];

          strings.forEach((s) => {
            const panelsList = s?.panels || [];
            panelsList.forEach(([roofIdx, panelIdx]) => {
              const roofIdxMap = stringingRoofIndexMap;
              const mappedRoofIdx = Array.isArray(roofIdxMap) ? roofIdxMap[roofIdx] : roofIdx;
              const vertexIdx = roofVertexIndices[mappedRoofIdx];
              if (vertexIdx === undefined) return;
              let actual = panelIdx;

              if (panelIndexMode === 'selected') {
                const mapArr = stringingSelectedPanelIndexMap?.[roofIdx];
                if (Array.isArray(mapArr)) {
                  actual = mapArr[panelIdx];
                } else {
                  // Fallback: use the selection snapshot (sorted) as the payload order
                  if (!selectedSortedCache[vertexIdx]) {
                    const raw = selectionSnapshot?.[vertexIdx] || [];
                    selectedSortedCache[vertexIdx] = Array.isArray(raw) ? [...raw].sort((a, b) => a - b) : [];
                  }
                  actual = selectedSortedCache[vertexIdx][panelIdx];
                }
              }
              if (actual === undefined || actual === null) return;
              ids.push(`${vertexIdx}-${actual}`);
            });
          });
          if (ids.length === 0) return;
          if (typeof addToSelectedObjectsBulk === 'function') {
            addToSelectedObjectsBulk(ids, 'panels', true);
          } else {
            addToSelectedObjects(ids, 'panels');
          }
        } catch (e) {
          // noop
        }
      },
      getSelectedPanelsByVertex: () => {
        try {
          return selectedObjects?.panels || {};
        } catch {
          return {};
        }
      },
      // 🆕 From our stash: Drawing capabilities access (for roof segments magnetization)
      drawingCapabilities,
      setDrawingCapabilities,
      // Designer capabilities access (for panel placement magnetization)
      designerCapabilities,
      setDesignerCapabilities,
      // Mode and tool setters from DesignContext (for obstacles/trees drawing)
      setMode,
      setTool,
      editTool,
      setEditTool,
      // Setback-related methods
      getSegments: () => segments,
      getSetbackMode: () => setbackMode,
      getFireSetbacks: () => fireSetbacks,
      getShowSetbacks: () => showSetbacks,
      // Selection methods
      getSelectedObjects: () => selectedObjects,
      clearSelection,
      // 🆕 Expose setViewState for direct canvas integration
      setViewState,
      // 🆕 Expose getViewMode for viewMode tracking
      getViewMode: () => viewMode,
      // 🆕 Progress tracking methods (for polling by parent)
      getShadingProgress: () => toolbar.current?.getProgress?.() || 0,
      isShadingRunning: () => toolbar.current?.isRunningShading?.() || false,
      isShadingCanceled: () => toolbar.current?.isCanceledShading?.() || false
    }));

    return (
      <>
        <DesignContext.Consumer>
          {(value) => {
            const newValue = {
              ...value,
              segments,
              obstacles,
              trees,
              imgSrc,
              rgbTexture,
              texture,
              dsmTexture,
              orgDSMTexture,
              selectedTexture,
              minDSM,
              maxDSM,
              dsmOffset,
              displacementBias,
              segmentSelection,
              pointSelection,
              lineSelection,
              panelSelection,
              setbackSelection,
              showSunLight,
              showGround,
              showTrees,
              showSetbacks,
              showPanels,
              showRoofs,
              showShading,
              showDSM,
              showDSMHD,
              showDSMHouse,
              showMeasurements,
              showAnnotation,
              moveSelection,
              insertObject,
              showVirtualPanels,
              center: [cx / c, cy / c],
              scale,
              imageSizeScale,
              month,
              shadingType,
              weather,
              materials,
              visible,
              moveStatus,
              isLoaded,
              loadingTitle,
              readonly,
              allowPanelToggleInReadonly,
              panelVisibility: value.panelVisibility ?? false,
              viewMode,
              resolution,
              selectionMode,
              initialZoom,
              setSelectionMode,
              selectedObjects,
              removeFromSelectedObjects,
              addToSelectedObjects,
              addToSelectedObjectsBulk,
              clearSelection,
              deleteSelectedObjects,
              setHeight,
              setPitch,
              setSetback,
              setSegmentPosition,
              changeEdgeTypes,
              moveRoofVertex,
              coordinates,
              setSegmentShadingFactors,
              accordion,
              setShadingTexture,
              shadingStatus,
              setShadingStatus,
              setSegmentParameters,
              optPOA,
              automaticAzimuthMode,
              orgVertices,
              vertices,
              vertices3D,
              verticesType,
              eaves,
              pitches,
              azimuths,
              eaveHeights,
              UV,
              changeClockwise,
              ranges,
              parents,
              cx,
              cy,
              c,
              inchePerUnit,
              footPerUnit,
              updateEavesByAzimuth,
              panelData,
              panelEaves,
              setbacksData,
              setbacks3dData,
              roofTransformations,
              gwidth,
              gheight,
              canvas,
              sceneRef,
              treeData,
              controls,
              labels,
              drawingCapabilities,
              designerCapabilities,
              viewerMode: !!viewerMode,
              viewState,
              annotations,
              iframeCallback,
              designFrame,
              colorSetbacksByEdgeType,
              getFootPerSceneUnit,
              getInchePerSceneUnit,
              getSceneUnitPerFoot,
              clearAllSelection,
              runOptimizer,
              panelPlacement,
              toggleView,
              deletePanels,
              rotatePanels,
              alignHorizontal,
              alignVertical,
              downloadJSON,
              downloadDXF,
              startShading,
              setMonth,
              setShadingType,
              setViewMode,
              loadShading,
              getSetbacks,
              updateStateFromSegments,
              magnetizeVertices,
              getBorders,
              addSelectedPoint,
              removeSelectedPoint,
              insertPanels,
              canInsertPanel,
              movePanels,
              addShape,
              editShapes,
              mergeShapes,
              splitShapes,
              editTool,
              setEditTool,
              deleteShape,
              deletePointFromShape,
              addPointToShape,
              setLineLabel,
              setDrawingCapabilities,
              setDesignerCapabilities,
              setMode,
              zoomCamera,
              switchAnnotation,
              setAnnotations,
              setViewState,
              annotationsNeutralPlacementCursor: annotationsNeutralPlacementCursor === true,
              stringingData,
              showStringing: showStringing || captureStringing,
              forceShowStringing: forceShowStringing || captureStringing,
              stringingPanelIndexMode,
              stringingSelectedPanelIndexMap,
              stringingRoofIndexMap,
              onTreeClick,
              footprint: footprint || footprintPolygon || footprint_polygon || lead?.property?.footprint || null,
              lead,
              designId
            };

            return (
              <DesignContext.Provider value={newValue}>
                {/* Toolbar: visible by default; hidden when hideToolbar (e.g. designer library), ref kept for progress */}
                {hideToolbar ? (
                  <div className="canvas-library-toolbar-hidden" aria-hidden="true">
                    <Toolbar ref={toolbar} />
                  </div>
                ) : (
                  <Toolbar ref={toolbar} />
                )}

                <div
                  ref={container}
                  className={`mx-auto ${visible ? 'position-relative container3d' : 'position-absolute container3d-invisible'} canvas3d`}
                >
                  <Canvas
                    orthographic={viewMode === '2D'}
                    shadows
                    dpr={window.devicePixelRatio}
                    gl={{ antialias: true }}
                    // shadowMap={{ type: THREE.VSMShadowMap }}
                    ref={canvas}
                    pixelRatio={window.devicePixelRatio}
                    camera={
                      viewMode === '2D'
                        ? {
                            position: [0, 100, 0],
                            up: [0, 1, 0],
                            zoom: initialZoom
                          }
                        : { position: [0, 100, 200], fov: 60, far: 1024 }
                    }
                    onCreated={({ gl, scene, camera, raycaster }) => {
                      gl.setClearColor('#f0f0f0');
                      gl.shadowMap.enabled = true;
                      patchRaycasterIntersectObjects(raycaster);
                      sceneRef.current = scene;
                      try {
                        if (camera?.type === 'OrthographicCamera') {
                          const z = Number(camera.zoom);
                          if (Number.isFinite(z) && z > 0 && camera.userData && camera.userData.baseZoom == null) {
                            camera.userData.baseZoom = z;
                          }
                        }
                      } catch (e) {}
                    }}
                  >
                    <Scene data={newValue} ref={sceneObjRef} />
                    <ExportWrapper ref={exportRef} sceneObjRef={sceneObjRef} textureSize={texture?.image?.width ?? 1024} />
                  </Canvas>
                </div>
              </DesignContext.Provider>
            );
          }}
        </DesignContext.Consumer>
      </>
    );
  }
);

Canvas3D.propTypes = {
  imgSrc: PropTypes.string,
  designId: PropTypes.string,
  canvasId: PropTypes.string,
  segments: PropTypes.array,
  obstacles: PropTypes.array,
  trees: PropTypes.array,
  panels: PropTypes.array,
  weatherDataset: PropTypes.string,
  shadingHasRun: PropTypes.bool,
  stringingData: PropTypes.any,
  showStringing: PropTypes.bool,
  forceShowStringing: PropTypes.bool,
  stringingPanelIndexMode: PropTypes.oneOf(['all', 'selected']),
  stringingSelectedPanelIndexMap: PropTypes.any,
  stringingRoofIndexMap: PropTypes.any,
  onLoaded: PropTypes.func,
  hideToolbar: PropTypes.bool,
  allowLeadShadingFallback: PropTypes.bool,
  keepShadingViewOnAccordionChange: PropTypes.bool,
  annotationsNeutralPlacementCursor: PropTypes.bool,
  colorSetbacksByEdgeType: PropTypes.bool,
  footprint: PropTypes.any,
  footprintPolygon: PropTypes.any,
  footprint_polygon: PropTypes.any
};

export default Canvas3D;
